import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Renderer, type QualityTier } from '../render/Renderer';
import { Stage } from '../render/Stage';
import { Post } from '../render/Post';
import { createMaterialProvider } from '../render/materials/provider';
import { LoaderUI } from './Loader';
import { CameraDirector } from './CameraDirector';

/** Point d'entrée applicatif : rendu, caméra, boucle, HUD. Le personnage s'y branche via `setCharacter`. */
export class App {
  readonly renderer: Renderer;
  readonly stage: Stage;
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: OrbitControls;
  readonly director: CameraDirector;
  post: Post;
  readonly loader = new LoaderUI();
  readonly materials: ReturnType<typeof createMaterialProvider>;
  /** Callbacks appelés chaque frame (présence, animation…). */
  readonly updaters: Array<(dt: number, t: number) => void> = [];
  private clock = new THREE.Clock();
  private fpsEl = document.getElementById('fps')!;
  private fpsAcc = 0;
  private fpsFrames = 0;
  private autoQuality = true;

  constructor(container: HTMLElement) {
    this.renderer = new Renderer(container);
    const q = this.renderer.quality;
    this.stage = new Stage(this.renderer.gl, q);

    // Objectif "portrait" (~70 mm) : flatteur pour le visage, peu de déformation.
    this.camera = new THREE.PerspectiveCamera(26, 1, 0.05, 60);
    this.camera.position.set(0.15, 1.52, 2.35);

    this.controls = new OrbitControls(this.camera, this.renderer.gl.domElement);
    this.controls.target.set(0, 1.38, 0);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    // Clic droit (ou deux doigts) : décaler la caméra ; borné pour ne jamais la perdre de vue.
    this.controls.enablePan = true;
    this.controls.screenSpacePanning = true;
    this.controls.panSpeed = 0.8;
    this.controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
    this.controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    this.controls.addEventListener('change', () => this.clampTarget());
    this.controls.minDistance = 0.55;
    this.controls.maxDistance = 5.5;
    this.controls.minPolarAngle = THREE.MathUtils.degToRad(35);
    this.controls.maxPolarAngle = THREE.MathUtils.degToRad(100);
    this.controls.rotateSpeed = 0.55;
    this.controls.zoomSpeed = 0.7;
    this.controls.update();
    this.director = new CameraDirector(this.camera, this.controls);

    this.post = new Post(this.renderer.gl, this.stage.scene, this.camera, q);
    this.materials = createMaterialProvider({
      renderer: this.renderer.gl, msaa: q.msaa > 0,
      maxTextureSize: q.tier === 'low' ? 1024 : q.tier === 'medium' ? 2048 : 4096,
    });

    this.renderer.onResize = (w, h, dpr) => {
      this.camera.aspect = w / Math.max(1, h);
      this.applyViewOffset();
      this.post.setSize(w, h);
      this.stage.setPixelRatio(dpr);
    };
    this.renderer.resize();
    this.bindHud();
  }

  private bindHud() {
    // Compteur de perfs : en dev, ou avec ?debug en production.
    if (!import.meta.env.DEV && !new URLSearchParams(location.search).has('debug')) this.fpsEl.style.display = 'none';
    const btn = document.getElementById('quality')!;
    const order: Array<QualityTier | 'auto'> = ['auto', 'low', 'medium', 'high', 'ultra'];
    let i = 0;
    btn.textContent = 'Auto · ' + this.renderer.quality.tier;
    btn.addEventListener('click', () => {
      i = (i + 1) % order.length;
      const v = order[i];
      this.autoQuality = v === 'auto';
      if (v !== 'auto') this.applyQuality(v);
      btn.textContent = v === 'auto' ? 'Auto · ' + this.renderer.quality.tier : v;
    });
  }

  private insets = { right: 0, bottom: 0, left: 0 };
  /** Zone d'écran masquée par un panneau : on décale le centre optique pour qu'elle reste centrée dans l'espace libre. */
  setInsets(right: number, bottom: number, left = this.insets.left) {
    this.insets = { right, bottom, left };
    // Feuille du bas (mobile) : il faut pouvoir reculer davantage pour la voir en entier.
    this.controls.maxDistance = bottom > 0 ? 9 : 5.5;
    this.applyViewOffset();
    const h = this.renderer.gl.domElement.clientHeight || 1;
    this.director.setViewFraction((h - bottom) / h);
  }

  private applyViewOffset() {
    const el = this.renderer.gl.domElement;
    const w = el.clientWidth, h = el.clientHeight;
    const { right, bottom, left } = this.insets;
    if (!right && !bottom && !left) this.camera.clearViewOffset();
    else this.camera.setViewOffset(w, h, (right - left) / 2, bottom / 2, w, h);
    this.camera.updateProjectionMatrix();
  }

  /** Garde le point visé dans une boîte autour d'elle (le décalage reste un cadrage, pas une fuite). */
  private clampTarget() {
    const t = this.controls.target;
    // (appelé aussi pendant la construction, avant le directeur)
    const fx = this.director?.focus.x ?? 0, fz = this.director?.focus.z ?? 0;
    const cx = THREE.MathUtils.clamp(t.x, fx - 0.9, fx + 0.9), cy = THREE.MathUtils.clamp(t.y, 0.15, 1.95), cz = THREE.MathUtils.clamp(t.z, fz - 0.6, fz + 0.6);
    if (cx !== t.x || cy !== t.y || cz !== t.z) {
      const dx = cx - t.x, dy = cy - t.y, dz = cz - t.z;
      t.set(cx, cy, cz);
      this.camera.position.x += dx; this.camera.position.y += dy; this.camera.position.z += dz;
    }
  }

  applyQuality(tier: QualityTier) {
    this.renderer.setQuality(tier);
    const q = this.renderer.quality;
    this.stage.key.shadow.mapSize.setScalar(q.shadowMapSize);
    this.stage.key.shadow.radius = q.shadowRadius;
    this.stage.key.shadow.map?.dispose();
    this.stage.key.shadow.map = null;
    this.stage.setDust(q.dust);
    this.post.build(q);
    this.renderer.resize();
  }

  start() {
    // setAnimationLoop : rAF à l'écran, images du casque en réalité virtuelle (WebXR).
    const loop = () => {
      const dt = Math.min(this.clock.getDelta(), 1 / 20);
      const t = this.clock.elapsedTime;
      const xr = this.renderer.gl.xr;
      if (xr.isPresenting) {
        // Le casque pilote la vue ; la caméra de l'app suit la tête (son regard, la proximité, le toucher).
        xr.getCamera().matrixWorld.decompose(this.camera.position, this.camera.quaternion, _xrScale);
        this.camera.updateMatrixWorld();
        for (const u of this.updaters) u(dt, t);
        this.stage.update(dt);
        // Réalité augmentée : pas de fond ni de brouillard, la caméra du téléphone se voit derrière elle.
        const sc = this.stage.scene, ar = xr.getSession()?.environmentBlendMode !== 'opaque';
        const bg = sc.background, fog = sc.fog;
        if (ar) { sc.background = null; sc.fog = null; }
        this.renderer.gl.render(sc, this.camera);
        if (ar) { sc.background = bg; sc.fog = fog; }
        return;
      }
      this.controls.update();
      this.director.update(dt);
      for (const u of this.updaters) u(dt, t);
      this.stage.update(dt);
      this.renderer.beginGpuTimer();
      this.post.render(dt);
      this.renderer.endGpuTimer();
      const now = performance.now();
      if (this.autoQuality) this.renderer.adapt(dt * 1000, now);
      this.fpsAcc += dt; this.fpsFrames++;
      if (this.fpsAcc > 0.5) {
        const gpu = this.renderer.lastGpuMs;
        this.fpsEl.textContent = Math.round(this.fpsFrames / this.fpsAcc) + ' fps · ' + (gpu > 0 ? gpu.toFixed(1) + ' ms GPU · ' : '') + this.renderer.dpr.toFixed(2) + 'x';
        this.fpsAcc = 0; this.fpsFrames = 0;
      }
    };
    this.renderer.gl.setAnimationLoop(loop);
  }
}

const _xrScale = new THREE.Vector3();
